import { useStore } from './use-store'

interface UseComponentEntriesOptions<T> {
  translationKeyFields: (keyof T & string)[]
  computeKeyPrefix: (entries: T[], entryIndex: number) => string
}

export function useComponentEntries<T> (
  getEntries: () => T[],
  options: UseComponentEntriesOptions<T>
) {
  const store = useStore()
  const { translationKeyFields, computeKeyPrefix } = options

  function removeEntry (index: number) {
    const entries = getEntries()

    // 1. Collect the deleted entry's translation key values
    const deleted = entries[index]
    const deletedKeys = translationKeyFields.map(field => String(deleted[field]))

    // 2. Splice from array
    entries.splice(index, 1)

    // 3-4. For each remaining entry, compute expected key prefix and collect renames
    const renames: { oldKey: string; newKey: string }[] = []
    const renamedTargets = new Set<string>()

    for (let i = index; i < entries.length; i++) {
      const entry = entries[i]
      const expectedPrefix = computeKeyPrefix(entries, i)

      for (const field of translationKeyFields) {
        const currentKey = String(entry[field])
        const expectedKey = expectedPrefix + '.' + field
        if (currentKey !== expectedKey) {
          renames.push({ oldKey: currentKey, newKey: expectedKey })
          renamedTargets.add(expectedKey);
          (entry as any)[field] = expectedKey
        }
      }
    }

    // 5. Commit renames
    if (renames.length > 0) {
      store.commit('campaigns/RENAME_TRANSLATION_KEYS', { renames })
    }

    // 6. Delete orphaned keys (deleted entry's keys that weren't overwritten by a rename)
    const keysToDelete = deletedKeys.filter(key => !renamedTargets.has(key))
    if (keysToDelete.length > 0) {
      store.commit('campaigns/DELETE_TRANSLATION_KEYS', { keys: keysToDelete })
    }
  }

  function nextKeyPrefix () {
    const entries = getEntries()
    return computeKeyPrefix(entries, entries.length)
  }

  return {
    removeEntry,
    nextKeyPrefix
  }
}
